import { useMemo } from 'react';
import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import { token } from '@atlaskit/tokens';
import { css } from '@emotion/react';
import { Icon } from '@components/shared/Icon';

import { svgBadge } from '../assets/badge';
import { getCourseProgress } from '../utils/utils';
import { CourseItem, LessonsCollection, ProgressForLessons } from '../types';

interface CourseProgressCircleProps {
  lessonsCollection: LessonsCollection;
  progressForLessons?: ProgressForLessons;
  reward: CourseItem['reward'];
  title: CourseItem['title'];
}

export const CourseProgressCircle: React.FC<CourseProgressCircleProps> = ({
  lessonsCollection,
  progressForLessons,
  reward,
  title,
}) => {
  const { progress, isStatusCompleted } = useMemo(
    () => getCourseProgress(lessonsCollection, progressForLessons),
    [lessonsCollection, progressForLessons],
  );

  return (
    <div css={progressCircleContainerStyles} data-testid="course-progress-circle">
      <CircularProgressbarWithChildren
        strokeWidth={8}
        value={progress}
        styles={buildStyles({
          pathColor: token('color.border.success'),
          trailColor: 'rgba(9, 30, 66, 0.14)',
        })}
        css={[isStatusCompleted && completedCourseProgressStyles]}
      >
        <div css={[imageBadgeContainerStyles, isStatusCompleted && completedCourseProgressStyles]}>
          <img css={imageStyles} data-testid="course-badge-image" src={reward?.badgeCdnUrl || svgBadge} alt={title} />
        </div>
      </CircularProgressbarWithChildren>
      {isStatusCompleted && <Icon type="check" color={token('color.icon.success')} />}
    </div>
  );
};

const completedCourseProgressStyles = css({
  opacity: '0.2',
});

const progressCircleContainerStyles = css({
  position: 'relative',
  maxWidth: '32px',

  span: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
  },
});

const imageBadgeContainerStyles = css({
  width: '100%',
  height: '100%',
  padding: token('space.100'),
});

const imageStyles = css({
  width: '100%',
  height: '100%',
  objectFit: 'contain',
  borderRadius: '50%',
});
